/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 15/05/2025 17:46
 *
 */

import {
  CommonUtils,
  DataModel,
  Factory,
  Logger,
  ModelProperty,
  PropertyType,
  RestService,
  SettingsService,
  Validation,
  ValidationMode,
  ValidationType,
} from '@infodata-group/inf-commons';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VigiguardStandardEntity } from '../model/vigiguard-standard-entity';

dayjs.extend(localizedFormat);

export class Tools {
  private static colors = [
    '#BF414E',
    CommonUtils.color('red'),
    '#BF513B',
    CommonUtils.color('orange'),
    '#C39C3C',
    CommonUtils.color('yellow'),
    '#2E8E76',
    CommonUtils.color('green'),
    '#378BAB',
    '#4FC1E9',
    '#7C69AA',
    CommonUtils.color('purple'),
    '#AD5E8B',
    CommonUtils.color('pink'),
    CommonUtils.color('grey-e3'),
    CommonUtils.color('grey-c8'),
    '#C9DBE8',
    CommonUtils.color('grey-ad'),
    CommonUtils.color('grey-99'),
    '#677575',
    CommonUtils.color('grey-4a'),
    CommonUtils.color('grey-1d'),
  ];
  public static getColor(index: number): string {
    if (this.colors[index]) {
      return this.colors[index];
    }
    // If out of range, generate random color
    return (
      '#' + (0x1000000 + Math.random() * 0xffffff).toString(16).slice(1, 7)
    );
  }
  public static copyToClipboard(data: string): void {
    try {
      if (typeof data !== 'string') {
        data = JSON.stringify(data);
      }
      navigator.clipboard.writeText(data).then(
        () => {
          Logger.w('Async: Copying to clipboard was successful!');
        },
        (err) => {
          Logger.e('Async: Could not copy text: ', err);
        },
      );
    } catch (e) {
      Logger.e(e);
    }
  }
  public static downloadJSON(data: string): void {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(data);
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', 'menu.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  public static isGenericEntityField(field: string): boolean {
    return (
      field === 'api' ||
      field === 'idField' ||
      field === 'labelField' ||
      field === 'displayId' ||
      field === 'displayLabel'
    );
  }
  public static convertTime(time: string[]): string[] {
    const times = [];
    for (const t of time) {
      const temp = t.split(':');
      if (temp.length === 2) {
        times.push(t);
      }
      if (temp.length === 3) {
        times.push(temp[0] + ':' + temp[1]);
      }
    }
    return times;
  }
  public static loadStandardSchema$(
    rest: RestService,
    factory: Factory<unknown>,
  ): Observable<DataModel> {
    return rest.options(factory.getApi()).pipe(
      map((model) => {
        // Create validation rule for primary key
        try {
          const property = model.find(model.getPrimaryKey());
          if (property.showOnForm) {
            property.validation = new Validation({
              type: ValidationType.regexp,
              mode: ValidationMode.error,
              // rule: '^[a-z0-9_-]+$',
              rule: '^(?!^NEW$)[A-Z0-9_.-]+$',
              message: 'Les caractères spéciaux ne sont pas autorisés',
            });
          }
        } catch (e) {
          Logger.e(`Unable to find primary key for model ${model.id}`);
        }

        // Find glbAtv field
        try {
          const glbAtvProp = model.find('glbAtv');
          glbAtvProp.showOnSearch = true;
          glbAtvProp.showOnForm = false;
          glbAtvProp.existOnTable = false;
          glbAtvProp.searchType = PropertyType.switch;
        } catch (e) {
          Logger.w(`Impossible to find glbAtv property on object ${model.id}`);
        }

        return model;
      }),
    );
  }
  public static getSearchDataModel(dataModel): DataModel {
    const searchProperties = dataModel.properties.filter((x) => x.showOnSearch);

    const newDataModel = new DataModel();
    for (const initialProperties of searchProperties) {
      // Copy to avoid reference update
      const prop = new ModelProperty(
        initialProperties.id,
        initialProperties.title,
        initialProperties.type,
      );
      // tslint:disable-next-line:forin
      for (const key in initialProperties) {
        if (
          key === 'formGroup' ||
          key === 'width' ||
          key === 'mandatory' ||
          key === 'column'
        ) {
          continue;
        }
        prop[key] = initialProperties[key];
      }
      prop.showOnForm = true;
      if (!prop.searchType) {
        prop.searchType = prop.editType;
      }
      if (prop.id === 'glbAtv') {
        prop.mandatory = true;
      }
      newDataModel.addProperty(prop);
    }
    return newDataModel;
  }
  public static errorObserver(e): void {
    Logger.e(`Rest error`, e);
  }
  public static checkResizeBox(
    settingsService: SettingsService,
    storageKey: string,
  ): void {
    if (!storageKey) {
      return;
    }
    const setting = settingsService.getSetting(`${storageKey}-resizeBox`);
    if (!setting) {
      return;
    }
    const maxWidth = Tools.getMaxWidthResizeBox();
    if (setting > maxWidth) {
      settingsService.addSetting(`${storageKey}-resizeBox`, maxWidth);
    }
  }
  public static getMaxWidthResizeBox(): number {
    return window.innerWidth - 330 - 150; // Max Menu - 150 static
  }

  public static checkRights(
    authorizedGroups: string[],
    userGroups: string[],
  ): boolean {
    return userGroups.filter((x) => authorizedGroups.includes(x)).length > 0;
  }

  public static processToolbarSubtitles(
    entity: VigiguardStandardEntity,
  ): { icon: string[]; label: string }[] {
    const subtitles = [];
    if (!entity) {
      return subtitles;
    }

    if (entity.createdAt) {
      subtitles.push({
        icon: ['clock', 'stream'],
        label: entity.createdAt || ' - ',
      });
    }
    if (entity.updatedAt) {
      subtitles.push({
        icon: ['edit'],
        label: entity.updatedAt || ' - ',
      });
    }
    return subtitles;
  }
}
